import { t } from "../../shared/trendReportShared";

// EN / 中文 / Both as a segmented control rather than a <select>.
// "Both" is a real target here, not a UI convenience: the backend sends that
// recipient two separate emails (see news_letter/dispatch.languages_for), and a
// three-way choice reads better inline than a dropdown you have to open to see.
//
// Three letters on their own say nothing about *what* is being switched, so
// every button carries a full-sentence tooltip and the control is never shown
// unlabelled. Two labelled forms, by how much room the caller has:
//   `label` + `hint` - block form, for the add modal where language is the
//                      primary decision being made.
//   `inlineLabel`    - a caption beside the buttons, for list rows where the
//                      language is a tweak to one existing audience.
export const LANGUAGE_OPTIONS = ["en", "zh", "both"];

const LABEL_KEY = {
  en: "newsletterLangEn",
  zh: "newsletterLangZh",
  both: "newsletterLangBoth",
};

const TITLE_KEY = {
  en: "newsletterLangEnFull",
  zh: "newsletterLangZhFull",
  both: "newsletterLangBothFull",
};

const PAD = {
  xs: "px-2 py-0.5 text-[10px]",
  sm: "px-2.5 py-1 text-[11px]",
  md: "px-4 py-2 text-xs",
};

export default function LanguagePicker({
  value,
  onChange,
  language,
  disabled = false,
  size = "sm",
  label,
  hint,
  inlineLabel,
}) {
  const pad = PAD[size] || PAD.sm;

  const control = (
    <div
      className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      role="group"
      aria-label={t("newsletterLanguageTitle", language)}
    >
      {LANGUAGE_OPTIONS.map((opt) => {
        const active = (value || "en") === opt;
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            title={t(TITLE_KEY[opt], language)}
            onClick={() => !active && onChange(opt)}
            className={`${pad} font-semibold transition-colors disabled:opacity-50 ${
              active
                ? "bg-orange-500 text-white"
                : "bg-white dark:bg-gray-900/40 text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-500/10"
            }`}
          >
            {t(LABEL_KEY[opt], language)}
          </button>
        );
      })}
    </div>
  );

  if (inlineLabel) {
    return (
      <span className="flex flex-col items-center gap-1.5 border border-gray-200 rounded-lg p-2">
        <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 whitespace-nowrap">
          {inlineLabel}
        </span>
        {control}
      </span>
    );
  }

  if (!label && !hint) return control;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {label && (
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            {label}
          </p>
        )}
        {control}
      </div>
      {hint && (
        <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      )}
    </div>
  );
}
